import {
  getSeriesRequest,
  type GetSeriesRequest,
} from '@core-api/presentation/presentation/series/{series_id}';
import { createPresentationSlideCompatibilityLayer } from '@mentimeter/compatibility/presentation';
import { useData } from '@mentimeter/core-hooks';
import { MentiError } from '@mentimeter/error-utils/error';
import type { Question } from '@mentimeter/http-clients';
import { useSlideFetcher } from '@mentimeter/migration-utils';
import type {
  QuestionWithSlide,
  Slide,
} from '@mentimeter/presentation-schema/api-types-overrides';
import { getRegionBySeriesId } from '@mentimeter/region';
import userCache from '@mentimeter/user';
import { useCallback, useMemo } from 'react';
import { type SWRConfiguration } from 'swr';
import invariant from 'tiny-invariant';
import { getPresentationSWRCacheKey } from '../../cache/getPresentationSWRCacheKey';

interface UsePresentationSlidesOptions {
  swrConfig?: SWRConfiguration;
  includeHiddenSlides?: boolean;
}

interface UsePresentationSlidesResult {
  slides: Array<QuestionWithSlide>;
  error: Error | null;
  revalidate: () => void;
  lazyGetSlides: () => Array<QuestionWithSlide>;
}

/**
 * Fetches a "question with a slide" with backwards compatibility to our legacy domain model.
 * @remarks Accepts a non-authenticated client and it should **only** be used within the presentation app.
 */
export const usePresentationSlides = (
  slideDeckPublicKey: string | undefined,
  options?: UsePresentationSlidesOptions,
): UsePresentationSlidesResult => {
  invariant(
    slideDeckPublicKey,
    'usePresentationSlides(SWR): slideDeckPublicKey is required',
  );
  const cacheKey = getPresentationSWRCacheKey('slides', { slideDeckPublicKey });
  const region = getRegionBySeriesId(slideDeckPublicKey);

  /**
   * We are extending the request from `getSeriesRequest` to include
   * the user's token.
   *
   * - If the presentation settings have been set to disable results sharing,
   *   and an unauthorized user tries to access the presentation, the user
   *   will get a `403 Forbidden` response.
   */
  const request = getSeriesRequest({ region, seriesId: slideDeckPublicKey });
  const userAuth = userCache.getToken();
  request.headers.append('Authorization', `Bearer ${userAuth}`);

  const fetcher = useSlideFetcher<Array<Question> | Array<Slide>>(
    slideDeckPublicKey,
    (_: GetSeriesRequest) => request,
  );

  const {
    data: slides,
    error,
    revalidate,
    lazyData,
  } = useData<Array<Question | Slide>>(
    {
      cacheKey,
      seriesId: slideDeckPublicKey,
      fetcher,
    },
    {
      keepPreviousData: true,
      suspense: true,
      revalidateIfStale: false,
      ...options?.swrConfig,
    },
  );

  if (!slides) {
    throw new MentiError(`Series response was empty`, { feature: 'live' });
  }

  const wrappedLazyData = useCallback(() => {
    const lazySlides = lazyData();
    return (
      lazySlides?.map((question) =>
        createPresentationSlideCompatibilityLayer(question),
      ) ?? []
    );
  }, [lazyData]);

  return useMemo(() => {
    return {
      slides: slides
        ?.map((slide) => createPresentationSlideCompatibilityLayer(slide))
        ?.filter((slide) => !slide.skip_slide || options?.includeHiddenSlides),
      error,
      revalidate,
      lazyGetSlides: wrappedLazyData,
    };
  }, [
    slides,
    error,
    revalidate,
    wrappedLazyData,
    options?.includeHiddenSlides,
  ]);
};
